import React, { useState, useEffect } from 'react';
import apiService from '../services/api.service';

const SuppliersList = () => {
  // States for supplier management
  const [suppliers, setSuppliers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [currentSupplier, setCurrentSupplier] = useState({
    id: null,
    name: '',
    contactName: '',
    phone: '',
    email: '',
    address: '',
    taxId: '',
    notes: '',
    isActive: true,
    categories: []
  });
  
  // UI states
  const [modalVisible, setModalVisible] = useState(false);
  const [modalMode, setModalMode] = useState('add'); // 'add' or 'edit'
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterActive, setFilterActive] = useState('all');

  // Fetch suppliers with filters
  const fetchSuppliers = async () => {
    setLoading(true);
    try {
      // Prepare filter parameters
      const filters = {};
      if (searchTerm) filters.name = searchTerm;
      if (filterCategory) filters.category = filterCategory;
      if (filterActive !== 'all') filters.isActive = filterActive === 'active';

      console.log('Loading suppliers...', filters);
      const response = await apiService.getSuppliers(filters);
      console.log('Supplier response:', response);
      setSuppliers(response.data);
      setError(null);
    } catch (err) {
      console.error('Error loading suppliers:', err);
      setError('Failed to load suppliers: ' + 
        (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  };

  // Fetch categories for dropdown
  const fetchCategories = async () => {
    try {
      const response = await apiService.getSupplierCategories();
      setCategories(response.data);
    } catch (err) {
      console.error('Error loading categories:', err);
      // No need to set error as this is secondary data
    }
  };

  // Load data on component mount
  useEffect(() => {
    fetchSuppliers();
    fetchCategories();
  }, []);

  // Reload suppliers when filters change
  useEffect(() => {
    fetchSuppliers();
  }, [searchTerm, filterCategory, filterActive]);

  // Handle supplier form input changes
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setCurrentSupplier({
      ...currentSupplier,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  // Handle supplier category selection
  const handleCategoryChange = (categoryName) => {
    const updatedCategories = currentSupplier.categories.includes(categoryName)
      ? currentSupplier.categories.filter(name => name !== categoryName)
      : [...currentSupplier.categories, categoryName];
    
    setCurrentSupplier({
      ...currentSupplier,
      categories: updatedCategories
    });
  };

  // Submit supplier form
  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (modalMode === 'add') {
        // Create new supplier
        await apiService.createSupplier(currentSupplier);
      } else {
        // Update existing supplier
        await apiService.updateSupplier(currentSupplier.id, currentSupplier);
      }

      // Success actions
      setModalVisible(false);
      resetForm();
      fetchSuppliers();
    } catch (err) {
      console.error('Error saving supplier:', err);
      setError('Failed to save supplier: ' + 
        (err.response?.data?.error || err.message));
    }
  };

  // Delete supplier
  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this supplier?')) {
      return;
    }

    try {
      await apiService.deleteSupplier(id);
      fetchSuppliers();
    } catch (err) {
      console.error('Error deleting supplier:', err);
      setError('Failed to delete supplier: ' + 
        (err.response?.data?.error || err.message));
    }
  };

  // Edit supplier
  const handleEdit = (supplier) => {
    setCurrentSupplier({
      id: supplier.id,
      name: supplier.name,
      contactName: supplier.contactName || '',
      phone: supplier.phone || '',
      email: supplier.email || '',
      address: supplier.address || '',
      taxId: supplier.taxId || '',
      notes: supplier.notes || '',
      isActive: supplier.isActive,
      categories: supplier.SupplierCategories 
        ? supplier.SupplierCategories.map(cat => cat.name) 
        : []
    });
    
    setModalMode('edit');
    setModalVisible(true);
  };

  // Open add supplier modal
  const handleAdd = () => {
    resetForm();
    setModalMode('add');
    setModalVisible(true);
  };

  // Reset supplier form
  const resetForm = () => {
    setCurrentSupplier({
      id: null,
      name: '',
      contactName: '',
      phone: '',
      email: '',
      address: '',
      taxId: '',
      notes: '',
      isActive: true,
      categories: []
    });
  };

  return (
    <div className="tab-content">
      <div className="content-header">
        <h3>Suppliers</h3>
        <button 
          className="add-button"
          onClick={handleAdd}
        >
          <span className="icon">+</span>
          <span className="button-text">Add New Supplier</span>
        </button>
      </div>

      <div className="filters">
        <div className="search-box">
          <input 
            type="text" 
            placeholder="Search suppliers..." 
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="filter-box">
          <label htmlFor="filterCategory">Category:</label>
          <select 
            id="filterCategory" 
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
          >
            <option value="">All</option>
            {categories.map(category => (
              <option key={category.id} value={category.name}>
                {category.name}
              </option>
            ))}
          </select>
        </div>

        <div className="filter-box">
          <label htmlFor="filterActive">Status:</label>
          <select 
            id="filterActive" 
            value={filterActive}
            onChange={(e) => setFilterActive(e.target.value)}
          >
            <option value="all">All</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>
      </div>

      {loading ? (
        <div className="loading">Loading...</div>
      ) : error ? (
        <div className="error-message">{error}</div>
      ) : (
        <div className="suppliers-list">
          <table className="data-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Contact</th>
                <th>Phone</th>
                <th>Email</th>
                <th>Categories</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {suppliers.length === 0 ? (
                <tr>
                  <td colSpan="7" className="no-data">No suppliers found.</td>
                </tr>
              ) : (
                suppliers.map(supplier => (
                  <tr key={supplier.id} className={supplier.isActive ? 'active-row' : 'inactive-row'}>
                    <td>{supplier.name}</td>
                    <td>{supplier.contactName || '-'}</td>
                    <td>{supplier.phone || '-'}</td>
                    <td>{supplier.email || '-'}</td>
                    <td>
                      {supplier.SupplierCategories && supplier.SupplierCategories.length > 0 
                        ? supplier.SupplierCategories.map(cat => cat.name).join(', ') 
                        : '-'}
                    </td>
                    <td>
                      <span className={`status-badge ${supplier.isActive ? 'active' : 'inactive'}`}>
                        {supplier.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button 
                          className="edit-button"
                          onClick={() => handleEdit(supplier)}
                          title="Edit"
                        >
                          <span className="icon">✎</span>
                        </button>
                        <button 
                          className="delete-button"
                          onClick={() => handleDelete(supplier.id)}
                          title="Delete"
                        >
                          <span className="icon">✕</span>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Supplier Modal */}
      {modalVisible && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>{modalMode === 'add' ? 'Add New Supplier' : 'Edit Supplier'}</h3>
              <button 
                className="close-button"
                onClick={() => setModalVisible(false)}
              >
                &times;
              </button>
            </div>
            
            <form className="form" onSubmit={handleSubmit}>
              <div className="form-section">
                <h4>Basic Information</h4>
                
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="name">Supplier Name *</label>
                    <input 
                      type="text" 
                      id="name" 
                      name="name" 
                      value={currentSupplier.name} 
                      onChange={handleInputChange} 
                      required 
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="contactName">Contact Person</label>
                    <input 
                      type="text" 
                      id="contactName" 
                      name="contactName" 
                      value={currentSupplier.contactName} 
                      onChange={handleInputChange} 
                    />
                  </div>
                </div>
                
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="phone">Phone</label>
                    <input 
                      type="text" 
                      id="phone" 
                      name="phone" 
                      value={currentSupplier.phone} 
                      onChange={handleInputChange} 
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="email">Email</label>
                    <input 
                      type="email" 
                      id="email" 
                      name="email" 
                      value={currentSupplier.email} 
                      onChange={handleInputChange} 
                    />
                  </div>
                </div>
                
                <div className="form-group">
                  <label htmlFor="address">Address</label>
                  <textarea 
                    id="address" 
                    name="address" 
                    value={currentSupplier.address} 
                    onChange={handleInputChange} 
                    rows="3"
                  ></textarea>
                </div>
                
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="taxId">Tax ID</label>
                    <input 
                      type="text" 
                      id="taxId" 
                      name="taxId" 
                      value={currentSupplier.taxId} 
                      onChange={handleInputChange} 
                    />
                  </div>
                  
                  <div className="form-group checkbox-group">
                    <label>
                      <input 
                        type="checkbox" 
                        name="isActive" 
                        checked={currentSupplier.isActive} 
                        onChange={handleInputChange} 
                      />
                      Active
                    </label>
                  </div>
                </div>
              </div>
              
              <div className="form-section">
                <h4>Categories</h4>
                
                <div className="categories-selection">
                  {categories.map(category => (
                    <label key={category.id} className="category-checkbox">
                      <input 
                        type="checkbox"
                        checked={currentSupplier.categories.includes(category.name)}
                        onChange={() => handleCategoryChange(category.name)}
                      />
                      {category.name}
                    </label>
                  ))}
                </div>
              </div>
              
              <div className="form-section">
                <h4>Notes</h4>
                
                <div className="form-group">
                  <textarea 
                    name="notes" 
                    value={currentSupplier.notes} 
                    onChange={handleInputChange} 
                    rows="4"
                    placeholder="Notes about this supplier..."
                  ></textarea>
                </div>
              </div>
              
              <div className="form-actions">
                <button 
                  type="button" 
                  className="cancel-button"
                  onClick={() => setModalVisible(false)}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="save-button"
                >
                  Save
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default SuppliersList;